import { computed, ComputedRef, ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";
import { storeToRefs } from "pinia";
import { useTasksStore } from "@/stores/v2";
import {
  VaccineConfiguration,
  Tank,
  TankGroup,
  GroupTag,
  ConfiguredVaccine,
  Vaccine,
  VaccineMethod
} from "@/types/v2";
import { isLikelyBathVaccinationConfiguration } from "@/utils/v2/tasks/display";

import { isVaccinationTask } from "@/stores/v2/sites/utils";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";

// Bath process status for tracking
export interface BathProcessStatus {
  configKey: string;
  status: 'not_started' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  expectedDuration: number; // in minutes
  actualDuration?: number; // in minutes
  isEarly?: boolean;
  isLate?: boolean;
}

export function useVaccinationConfigurations() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = storeToRefs(useTasksStore());

  // ===================================================================
  // Types
  // ===================================================================
  type VaccineUpdateKey = keyof ConfiguredVaccine;
  type ConfigUpdateKey = keyof VaccineConfiguration;
  type UpdateKey = VaccineUpdateKey | ConfigUpdateKey;

  // ===================================================================
  // Constants
  // ===================================================================
  const CONTEXT_VACCINATION_CONFIGURATION = "VaccinationConfiguration";

  // ===================================================================
  // Composables
  // ===================================================================
  const { getHistory, recordChange } = useTaskHistory();

  // ===================================================================
  // State
  // ===================================================================
  const configurations: ComputedRef<VaccineConfiguration[]> = computed(() => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];
    return activeTask.value.details.configurations || [];
  });

  // Bath configurations (filter for bath method)
  const bathConfigurations: ComputedRef<VaccineConfiguration[]> = computed(() => {
    return configurations.value.filter(isLikelyBathVaccinationConfiguration);
  });

  // Bath process status tracking (reactive state)
  const bathProcessStatuses = ref<Record<string, BathProcessStatus>>({});

  // Initialize status for bath configurations
  const initializeBathStatuses = () => {
    bathConfigurations.value.forEach(config => {
      if (!bathProcessStatuses.value[config.key]) {
        let status: 'not_started' | 'in_progress' | 'completed' = 'not_started';
        let startedAt: string | undefined;
        let completedAt: string | undefined;

        // Check actual bathProcess state
        const bathConfig = config as any;
        if (bathConfig.bathProcess?.completedAt) {
          status = 'completed';
          completedAt = bathConfig.bathProcess.completedAt;
          startedAt = bathConfig.bathProcess.startedAt;
        } else if (bathConfig.bathProcess?.startedAt) {
          status = 'in_progress';
          startedAt = bathConfig.bathProcess.startedAt;
        }

        bathProcessStatuses.value[config.key] = {
          configKey: config.key,
          status,
          startedAt,
          completedAt,
          expectedDuration: (bathConfig.duration || 24) * 60, // Convert hours to minutes
        };
      }
    });
  };

  // Auto-initialize when configurations change
  watch(bathConfigurations, initializeBathStatuses, { immediate: true });

  // ===================================================================
  // Actions
  // ===================================================================
  // Vaccination Configuration Factory
  const createEmptyConfiguration = (): VaccineConfiguration => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) {
      throw new Error("Not a vaccination task");
    }

    const config: VaccineConfiguration = {
      key: uuidv4(),
      vaccines: [],
      sourceTank: { key: uuidv4(), name: "" } as Tank,
      sourceGroup: { key: uuidv4(), name: "" } as TankGroup,
      targetAmount: 0,
      destinationTank: { key: uuidv4(), name: "" } as Tank,
      destinationGroup: { key: uuidv4(), name: "" } as TankGroup,
    };

    activeTask.value.details.configurations.push(config);

    return config;
  };

  // Update Vaccine Property in Vaccination Configuration
  const updateVaccineProperty = (
    vaccine: ConfiguredVaccine,
    key: VaccineUpdateKey,
    item: unknown
  ): void => {
    switch (key) {
      case "vaccine":
      case "method":
        vaccine[key] = {
          ...vaccine[key],
          name: (item as Vaccine | VaccineMethod).name,
        } as (typeof vaccine)[typeof key];
        break;
      case "dose":
        vaccine.dose = item as number;
        break;
    }
  };

  // Update Tanks, Groups & Target Amount in Vaccination Configuration
  const updateConfigurationProperty = (
    configuration: VaccineConfiguration,
    key: ConfigUpdateKey,
    item: unknown
  ): void => {
    switch (key) {
      case "sourceTank":
      case "destinationTank":
        configuration[key] = item as Tank;
        break;
      case "sourceGroup":
      case "destinationGroup":
        configuration[key] = item as TankGroup;
        break;
      case "targetAmount":
        configuration[key] = item as number;
        break;
    }
  };

  // Update configuration properties
  const updateConfiguration = async (
    configIndex: number,
    nestedIndex: number,
    key: UpdateKey,
    item: unknown
  ): Promise<void> => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value) || !item)
      return Promise.resolve();

    const configuration = configurations.value[configIndex];
    if (!configuration) return Promise.resolve();

    if (nestedIndex !== -1 && configuration.vaccines) {
      const vaccine = configuration.vaccines[nestedIndex];
      if (vaccine) {
        updateVaccineProperty(
          vaccine,
          key as VaccineUpdateKey,
          item
        );
      }
    } else {
      updateConfigurationProperty(
        configuration,
        key as ConfigUpdateKey,
        item
      );
    }
  };

  // Append new tag to Destination Group
  const appendTagToDestinationGroup = async (
    configIndex: number,
    newTag: GroupTag
  ): Promise<boolean> => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) {
      return false;
    }

    const configuration = configurations.value[configIndex];
    if (!configuration) {
      return false;
    }

    if (newTag.title === "Start Tag") {
      return false;
    }

    const updatedDestGroup = configuration.destinationGroup
      ? { ...configuration.destinationGroup }
      : { key: uuidv4(), name: "" } as TankGroup;

    if (!updatedDestGroup.tags) {
      updatedDestGroup.tags = [];
    } else {
      updatedDestGroup.tags = [...updatedDestGroup.tags];
    }

    const tagExists = updatedDestGroup.tags.some(tag => tag.title === newTag.title);

    if (!tagExists) {
      updatedDestGroup.tags.push(newTag);

      await updateConfiguration(configIndex, -1, 'destinationGroup', updatedDestGroup);
      return true;
    }

    return false;
  };

  // ===================================================================
  // Bath Process Actions
  // ===================================================================
  // Start bath process
  const startBathProcess = async (configIndex: number): Promise<boolean> => {
    const config = configurations.value[configIndex];
    if (!config) {
      return false;
    }

    if (!activeTask.value || !isVaccinationTask(activeTask.value)) {
      return false;
    }

    const startTime = new Date().toISOString();

    // Update the activeTask.value directly
    const taskConfig = activeTask.value.details.configurations[configIndex] as any;

    // Initialize bathProcess object if it doesn't exist
    if (!taskConfig.bathProcess) {
      taskConfig.bathProcess = {};
    }

    // Set the startedAt timestamp
    taskConfig.bathProcess.startedAt = startTime;

    // Record the change for history tracking
    const success = await recordChange({
      fieldPath: `configurations[${configIndex}].bathProcess.startedAt`,
      fieldName: 'Bath Process Started',
      oldValue: null,
      newValue: startTime,
      context: 'VaccinationConfiguration',
      contextType: 'execution',
      workflowStage: 'bath_process',
      requireJustification: false,
      justification: ''
    });

    if (success) {
      // Update the local status tracking
      bathProcessStatuses.value[config.key] = {
        configKey: config.key,
        status: 'in_progress',
        startedAt: startTime,
        expectedDuration: (taskConfig.duration || 24) * 60, // Convert hours to minutes
      };

      console.log(`Started bath process for config ${config.key} - Duration: ${taskConfig.duration || 24} hours`);
      return true;
    } else {
      // If recordChange failed, revert the direct update
      if (taskConfig.bathProcess) {
        delete taskConfig.bathProcess.startedAt;
      }
      return false;
    }
  };

  const finishBathProcess = async (configIndex: number): Promise<boolean> => {
    const config = configurations.value[configIndex];

    if (!config) {
      return false;
    }

    if (!activeTask.value || !isVaccinationTask(activeTask.value)) {
      return false;
    }

    const currentStatus = bathProcessStatuses.value[config.key];
    if (!currentStatus || !currentStatus.startedAt) {
      console.error('Cannot finish process that was not started');
      return false;
    }

    const startTime = new Date(currentStatus.startedAt);
    const taskConfig = activeTask.value.details.configurations[configIndex] as any;
    const expectedHours = taskConfig.duration || 24;
    const expectedMs = expectedHours * 60 * 60 * 1000;
    const actualMs = Date.now() - startTime.getTime();
    const isEarly = actualMs < expectedMs * 0.95;
    const isLate = actualMs > expectedMs * 1.05;
    const requiresJustification = isEarly || isLate;

    let fieldName = 'Bath Process Completed';
    if (isEarly) {
      const minutesEarly = Math.round((expectedMs - actualMs) / (60 * 1000));
      fieldName = `Ending Bath Process Early (${minutesEarly} minutes before expected completion)`;
    } else if (isLate) {
      const minutesLate = Math.round((actualMs - expectedMs) / (60 * 1000));
      fieldName = `Ending Bath Process Late (${minutesLate} minutes after expected completion)`;
    }

    const finishTimeISO = new Date().toISOString();

    if (!taskConfig.bathProcess) {
      taskConfig.bathProcess = {};
    }

    taskConfig.bathProcess.completedAt = finishTimeISO;

    const success = await recordChange({
      fieldPath: `configurations[${configIndex}].bathProcess.completedAt`,
      fieldName: fieldName,
      oldValue: null,
      newValue: finishTimeISO,
      context: 'VaccinationConfiguration',
      contextType: 'execution',
      workflowStage: 'bath_process',
      requireJustification: requiresJustification,
      justification: ''
    });

    if (success) {
      bathProcessStatuses.value[config.key] = {
        ...currentStatus,
        status: 'completed',
        completedAt: finishTimeISO,
        actualDuration: Math.round(actualMs / (60 * 1000)),
        isEarly,
        isLate,
      };

      return true;
    } else {
      if (taskConfig.bathProcess) {
        delete taskConfig.bathProcess.completedAt;
      }
      return false;
    }
  };

  // Get bath process status
  const getBathProcessStatus = (configKey: string): BathProcessStatus | null => {
    return bathProcessStatuses.value[configKey] || null;
  };

  // ===================================================================
  // Getters
  // ===================================================================
  // Get Vaccination Configuration title
  const getConfigurationTitle = (configuration: VaccineConfiguration): string => {
    // Check if this is a bath configuration
    if ('isSimplifiedBath' in configuration || 'isStandardBath' in configuration) {
      const bathConfig = configuration as any;
      
      if (bathConfig.isSimplifiedBath && bathConfig.tank) {
        return `${bathConfig.tank.name || bathConfig.tank.label || "Unknown Tank"}`;
      } else if (bathConfig.isStandardBath && bathConfig.sourceTank && bathConfig.destinationTank) {
        return `${bathConfig.sourceTank.name || "Unknown"} → ${bathConfig.destinationTank.name || "Unknown"}`;
      }
      
      return "Bath Configuration";
    }
    
    // Standard vaccination configuration
    return (
      configuration.destinationTank?.label ||
      configuration.destinationTank?.name ||
      "New Configuration"
    );
  };

  // Get Vaccination Configuration Tags
  const getConfigurationTags = (configuration: VaccineConfiguration): GroupTag[] => {
    const sourceTags = configuration.sourceGroup?.tags || [];
    const destinationTags = configuration.destinationGroup?.tags || [];

    const uniqueTags = new Map<string, GroupTag>();

    sourceTags
      .filter(tag => tag.title !== "Start Tag")
      .forEach(tag => {
        if (!uniqueTags.has(tag.title)) {
          uniqueTags.set(tag.title, tag);
        }
      });

    destinationTags
      .filter(tag => tag.title !== "Start Tag")
      .forEach(tag => {
        if (!uniqueTags.has(tag.title)) {
          uniqueTags.set(tag.title, tag);
        }
      });

    // Convert the map values back to an array
    return Array.from(uniqueTags.values());
  };

  // Get Vaccination Configuration total weight
  const getConfigurationTotalWeight = (configKey: string): number => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return 0;

    const weightConfiguration = activeTask.value.details.weightConfiguration;
    if (!weightConfiguration?.progressData) return 0;

    return weightConfiguration.progressData
      .filter((record) => record.configurationKey === configKey)
      .reduce((sum, record) => sum + record.weight, 0);
  };

  // Get Vaccination Configurations Records History
  const getVaccinationConfigurationsHistory = () => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];

    const fullHistory = getHistory();

    return fullHistory.filter(item => item.context === CONTEXT_VACCINATION_CONFIGURATION);
  }

  // Get Vaccination Records History
  const getVaccinationRecordHistory = (configKey: string, entryIndex: number) => {
    if (!activeTask.value || !isVaccinationTask(activeTask.value)) return [];

    const configIndex = activeTask.value.details.configurations.findIndex(
      config => config.key === configKey
    );

    if (configIndex === -1) return [];

    const allHistory = activeTask.value.history?.entries || [];

    return allHistory.filter(entry => {
      return entry.fieldPath.includes(`configurations[${configIndex}].progressData[${entryIndex}]`) &&
        (entry.fieldName === 'Administered IVPs' ||
          entry.fieldName === 'Weight' ||
          entry.fieldName === 'IVP Administration');
    }).map(entry => {

      return {
        field: entry.fieldName,
        oldValue: entry.oldValue,
        newValue: entry.newValue,
        reason: entry.justification,
        userName: entry.userName,
        timestamp: entry.timestamp
      };
    });
  };


  return {
    // ===================================================================
    // State
    // ===================================================================
    configurations,
    bathConfigurations,
    bathProcessStatuses,

    // ===================================================================
    // Getters
    // ===================================================================
    getConfigurationTitle,
    getConfigurationTags,
    getConfigurationTotalWeight,
    getVaccinationRecordHistory,
    getVaccinationConfigurationsHistory,
    getBathProcessStatus,

    // ===================================================================
    // Actions
    // ===================================================================
    createEmptyConfiguration,
    updateConfiguration,
    appendTagToDestinationGroup,

    // ===================================================================
    // Bath Process Actions
    // ===================================================================
    startBathProcess,
    finishBathProcess,
  };
}
